import { api } from './client'

export type PackagingTrackingMode = 'lot' | 'quantity'

export interface PackagingMaterial {
    id: string
    sku: string
    barcode?: string | null
    name: string
    unit: string
    min_stock: number
    /** Đơn giá bán vật tư (giá vật tư) khi tính vào đơn. 0 = không tính tiền. */
    sale_price: number
    notes?: string | null
    is_active: boolean
    is_for_packaging: boolean
    tracking_mode: PackagingTrackingMode
    created_at: string
    updated_at: string
    /** Tồn khả dụng gộp từ các lô đang hoạt động. Chỉ endpoint list trả về;
     *  get/create/update không có nên để optional. */
    stock_available?: number
}

export interface ListPackagingMaterialsParams {
    search?: string
    is_active?: boolean
    page?: number
    per_page?: number
}

export interface CreatePackagingMaterialRequest {
    sku: string
    barcode?: string | null
    name: string
    unit: string
    min_stock: number
    /** Đơn giá bán vật tư (giá vật tư). 0 = không tính tiền vào đơn. */
    sale_price: number
    notes?: string | null
    is_active: boolean
    is_for_packaging: boolean
    tracking_mode?: PackagingTrackingMode
}

export type UpdatePackagingMaterialRequest = CreatePackagingMaterialRequest

export type BOMScope = 'per_unit' | 'per_order'

// BOMSummary — info gọn cho mỗi product hiện trong list BOM screen.
export interface BOMSummary {
    count: number
}

export interface PackagingBOMItem {
    id: string
    product_id: string
    packaging_material_id: string
    quantity: number
    scope: BOMScope
    // pack_size — số SP dùng chung 1 lô vật tư này (spec 2026-07-06). Chỉ áp
    // cho scope=per_unit khi suggest lúc tạo đơn: needed = ceil(SP.qty / pack_size) × quantity.
    // Default 1 → hành vi cũ 1:1.
    pack_size: number
    notes?: string | null
    created_at: string
    updated_at: string
    material_sku?: string
    material_name?: string
    material_unit?: string
    /** Vật tư đóng gói SP (deduct ở PNK). Dùng để khoá pack_size=1 + tính giá vật tư. */
    material_is_for_packaging?: boolean
    /** Đơn giá bán vật tư (giá vật tư) hydrate từ master. */
    material_sale_price?: number
}

export interface BOMItemInput {
    packaging_material_id: string
    quantity: number
    scope?: BOMScope
    pack_size?: number
    notes?: string | null
}

export interface ReplaceBOMRequest {
    items: BOMItemInput[]
}

// --- Phase 2 (refactor): lot-based stock-in / lots / inventory / transactions ---

export interface PackagingLot {
    id: string
    packaging_material_id: string
    warehouse_id: string
    lot_code: string
    supplier_name?: string | null
    qty_in: number
    qty_remaining: number
    stock_in_note_id?: string | null
    notes?: string | null
    is_legacy: boolean
    created_at: string
    updated_at: string
    material_sku?: string
    material_name?: string
    material_unit?: string
    warehouse_name?: string
}

export interface PackagingStockInNote {
    id: string
    code: string
    warehouse_id: string
    notes?: string | null
    created_by_id?: string | null
    created_at: string
    lots?: PackagingLot[]
    lot_count?: number
    total_qty?: number
    warehouse_name?: string
}

export interface StockInLotInput {
    packaging_material_id: string
    lot_code?: string | null
    supplier_name?: string | null
    quantity: number
    notes?: string | null
}

export interface CreatePackagingStockInRequest {
    // Optional — BE auto-fills default warehouse khi missing. FE TUYỆT ĐỐI
    // không được gửi chuỗi rỗng "" (Go fail khi parse uuid.UUID), omit field.
    warehouse_id?: string
    notes?: string | null
    lots: StockInLotInput[]
}

export interface ListPackagingStockInParams {
    warehouse_id?: string
    date_from?: string
    date_to?: string
    page?: number
    per_page?: number
}

export interface PackagingStockOutNote {
    id: string
    code: string
    warehouse_id: string
    sales_order_id?: string | null
    reason?: string | null
    notes?: string | null
    total_qty: number
    created_by_id?: string | null
    created_at: string
    warehouse_name?: string
    order_code?: string
}

export interface PackagingStockOutItem {
    packaging_material_id: string
    material_sku: string
    material_name: string
    material_unit: string
    packaging_lot_id: string
    lot_code: string
    quantity: number
}

export interface PackagingStockOutDetail {
    note: PackagingStockOutNote
    items: PackagingStockOutItem[]
}

export interface ListPackagingStockOutParams {
    warehouse_id?: string
    sales_order_id?: string
    date_from?: string
    date_to?: string
    page?: number
    per_page?: number
}

export interface ListPackagingLotsParams {
    material_id?: string
    warehouse_id?: string
    status?: 'all' | 'active' | 'expired' | 'empty'
    search?: string
    page?: number
    per_page?: number
}

export interface PackagingInventoryRow {
    id: string
    packaging_material_id: string
    warehouse_id: string
    quantity: number
    updated_at: string
    material_sku?: string
    material_name?: string
    material_unit?: string
    material_barcode?: string | null
    material_min_stock?: number
    warehouse_name?: string
}

export interface ListPackagingInventoryParams {
    warehouse_id?: string
    search?: string
    low_stock_only?: boolean
    page?: number
    per_page?: number
}

export interface PackagingTransaction {
    id: string
    packaging_material_id: string
    warehouse_id: string
    packaging_lot_id?: string | null
    type: string
    quantity_change: number
    quantity_before: number
    quantity_after: number
    ref_id?: string | null
    ref_type?: string | null
    notes?: string | null
    created_by_id?: string | null
    created_at: string
    material_sku?: string
    material_name?: string
    material_unit?: string
    lot_code?: string
}

export interface ListPackagingTransactionsParams {
    material_id?: string
    warehouse_id?: string
    lot_id?: string
    type?: string
    date_from?: string
    date_to?: string
    page?: number
    per_page?: number
}

export type PackagingMovementGroupBy = 'day' | 'week' | 'month' | 'year'

export interface PackagingMovementParams {
    warehouse_id?: string
    date_from?: string
    date_to?: string
    group_by?: PackagingMovementGroupBy
}

export interface PackagingMovementRow {
    period: string
    stock_in: number
    stock_out: number
}

export interface PackagingMovement {
    rows: PackagingMovementRow[]
    total_stock_in: number
    total_stock_out: number
}

export interface PackagingBreakdownEntity {
    id: string
    sku: string
    name: string
    unit: string
    total_in: number
    total_out: number
}

export interface PackagingBreakdownPeriodRow {
    period: string
    stock_in: number
    stock_out: number
    breakdown_in: Record<string, number>
    breakdown_out: Record<string, number>
}

export interface PackagingMovementBreakdown {
    rows: PackagingBreakdownPeriodRow[]
    top_entities: PackagingBreakdownEntity[]
}

export const packagingApi = {
    listMaterials: (params?: ListPackagingMaterialsParams) =>
        api.get<PackagingMaterial[]>('/packaging-materials', {
            params: params as Record<
                string,
                string | number | boolean | undefined
            >,
        }),
    createMaterial: (data: CreatePackagingMaterialRequest) =>
        api.post<PackagingMaterial>('/packaging-materials', data),
    getMaterial: (id: string) =>
        api.get<PackagingMaterial>(`/packaging-materials/${id}`),
    updateMaterial: (id: string, data: UpdatePackagingMaterialRequest) =>
        api.put<PackagingMaterial>(`/packaging-materials/${id}`, data),
    deleteMaterial: (id: string) =>
        api.delete<void>(`/packaging-materials/${id}`),

    getBOM: (productId: string) =>
        api.get<PackagingBOMItem[]>(`/products/${productId}/packaging-bom`),
    replaceBOM: (productId: string, data: ReplaceBOMRequest) =>
        api.put<PackagingBOMItem[]>(
            `/products/${productId}/packaging-bom`,
            data,
        ),
    bomCounts: (productIds: string[]) =>
        api.post<Record<string, BOMSummary>>('/packaging-bom/counts', {
            product_ids: productIds,
        }),

    listStockIn: (params?: ListPackagingStockInParams) =>
        api.get<PackagingStockInNote[]>('/packaging-stock-in-notes', {
            params: params as Record<string, string | number | undefined>,
        }),
    getStockIn: (id: string) =>
        api.get<PackagingStockInNote>(`/packaging-stock-in-notes/${id}`),
    createStockIn: (data: CreatePackagingStockInRequest) =>
        api.post<PackagingStockInNote>('/packaging-stock-in-notes', data),

    listStockOut: (params?: ListPackagingStockOutParams) =>
        api.get<PackagingStockOutNote[]>('/packaging-stock-out-notes', {
            params: params as Record<string, string | number | undefined>,
        }),
    getStockOut: (id: string) =>
        api.get<PackagingStockOutDetail>(`/packaging-stock-out-notes/${id}`),

    listLots: (params?: ListPackagingLotsParams) =>
        api.get<PackagingLot[]>('/packaging-lots', {
            params: params as Record<string, string | number | undefined>,
        }),
    getLot: (id: string) => api.get<PackagingLot>(`/packaging-lots/${id}`),

    listInventory: (params?: ListPackagingInventoryParams) =>
        api.get<PackagingInventoryRow[]>('/packaging-inventory', {
            params: params as Record<
                string,
                string | number | boolean | undefined
            >,
        }),

    listTransactions: (params?: ListPackagingTransactionsParams) =>
        api.get<PackagingTransaction[]>('/packaging-transactions', {
            params: params as Record<string, string | number | undefined>,
        }),
    movement: (params?: PackagingMovementParams) =>
        api.get<PackagingMovement>('/packaging-transactions/movement', {
            params: params as Record<string, string | number | undefined>,
        }),
    movementBreakdown: (
        params?: PackagingMovementParams & { top_n?: number },
    ) =>
        api.get<PackagingMovementBreakdown>(
            '/packaging-transactions/movement-breakdown',
            { params: params as Record<string, string | number | undefined> },
        ),
    checkAvailability: (
        items: { line_key?: string; product_id: string; quantity: number }[],
    ) =>
        api.post<PackagingAvailability>(
            '/sales-orders/check-packaging-availability',
            { items },
        ),
    checkSurcharge: (
        items: { line_key?: string; product_id: string; quantity: number }[],
    ) =>
        api.post<PackagingSurcharge>(
            '/sales-orders/check-packaging-surcharge',
            { items },
        ),
}

// Phase 4 — suggestion kết quả (autofill UI lúc tạo đơn). BOM chỉ là gợi ý,
// sale có toàn quyền edit. Bỏ semantic required/has_blocking_required vì sale
// có thể bỏ bất kỳ vật tư nào (vd: khách không cần túi).
export interface PackagingAvailabilityItem {
    /** Khoá dòng đơn (create: field.id, edit: order_item.id). Rỗng cho POS.
     *  Vật tư gợi ý theo BOM của dòng SP → render inline dưới đúng sản phẩm. */
    line_key: string
    product_id: string
    material_id: string
    material_sku: string
    material_name: string
    material_unit: string
    suggested_qty: number
    available: number
    shortage: number
    /** Đơn giá bán vật tư (giá vật tư); >0 → tính tiền vào đơn. */
    unit_price: number
}

export interface PackagingAvailability {
    sufficient: boolean
    items: PackagingAvailabilityItem[]
}

// Vật tư đóng gói SP có giá bán (giá vật tư) theo BOM — dòng KHOÁ (charge_only)
// để tính tiền vào đơn, KHÔNG trừ kho (đã trừ ở PNK thành phẩm).
export interface PackagingSurchargeItem {
    /** Khoá dòng đơn (create: field.id, edit: order_item.id). Rỗng cho POS. */
    line_key: string
    product_id: string
    material_id: string
    material_name: string
    material_unit: string
    unit_price: number
    quantity: number
    line_amount: number
    /** true = vật tư đóng gói SP (khoá SL, không trừ kho). false = vật tư thường
     *  có giá (SL sửa được, trừ kho, có tồn). */
    charge_only: boolean
    /** Tồn kho + thiếu hụt — chỉ có ý nghĩa khi charge_only=false. */
    available: number
    shortage: number
}

export interface PackagingSurcharge {
    items: PackagingSurchargeItem[]
}
